import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  IonModal,
  IonButtons,
  IonButton,
  IonContent,
  IonGrid,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonRow,
  IonCol,
  IonInput,
  IonTextarea,
  IonCheckbox,
  IonIcon,
  IonAlert,
  IonSpinner,
} from '@ionic/react';
import {
  closeOutline,
  chevronForwardOutline,
  trashOutline,
} from 'ionicons/icons';
import CopyableIdTitle from '../../components/CopyableIdTitle';
import { format } from 'date-fns';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { callAdminApi } from '@rsmaids/shared/services/api/wixAdminApi';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { useAuth } from '../../contexts';
import BasePageWithHeader from '../../components/BasePageWithHeader';
import MiniCalendarPicker from '../../components/MiniCalendarPicker';
import GcalTimePicker from '../../components/GcalTimePicker';
import styles from './BlockedTimeForm.module.scss';
import { useTranslation } from 'react-i18next';
import { dfLocale } from '../../i18n/locale';

type StaffOption = { id: string; name: string; scheduleId: string; email: string };
type OptionPickerKey = 'repeatInterval' | 'staff' | 'availability' | null;

type PickerRect = { top: number; left?: number; right?: number };

// Positions a plain in-DOM dropdown (not IonPopover — see .calendarDropdown
// in the scss for why) by portaling it into .pickerPortalRoot — a bare,
// invisible position:fixed div rendered directly inside this form's
// <IonModal> (not inside .formContainer/.card, so it can never be clipped
// by either). The portal exists purely so both it and the trigger can be
// measured with the exact same getBoundingClientRect() call and the
// dropdown positioned with plain position:absolute relative to it — that
// avoids needing to predict how ion-modal's `contain: strict` (confirmed in
// @ionic/core's modal.md.css/modal.ios.css) reinterprets position:fixed
// coordinates, which is what made earlier attempts render detached.
//
// Recomputes on scroll/resize (capture:true so it catches scrolling of
// .formContainer specifically) so the dropdown tracks its trigger and
// scrolls along with the form instead of drifting away from it.
// Dismiss uses pointerdown (not mousedown/click) since this is a touch app.
function usePickerDropdown(
  isOpen: boolean,
  fieldRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  close: () => void,
  align: 'left' | 'right',
) {
  const [rect, setRect] = useState<PickerRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const field = fieldRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      setRect(align === 'left'
        ? { top: box.bottom - anchor.top + 4, left: box.left - anchor.left }
        : { top: box.bottom - anchor.top + 4, right: anchor.right - box.right });
    };
    update();

    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      // The dropdown itself is portaled into portalRef, not a descendant
      // of fieldRef anymore — a tap/scroll inside it must not count as
      // "outside" (that was closing it mid-scroll).
      if (fieldRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      document.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, fieldRef, portalRef, close, align]);

  return rect;
}

// Wraps usePickerDropdown + the portal/positioning boilerplate into a
// single component so each of the 4 pickers below is one tag instead of a
// repeated ~15-line block.
function PickerDropdown({
  isOpen,
  fieldRef,
  portalRef,
  close,
  align,
  className,
  children,
}: {
  isOpen: boolean;
  fieldRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  close: () => void;
  align: 'left' | 'right';
  className: string;
  children: React.ReactNode;
}) {
  const rect = usePickerDropdown(isOpen, fieldRef, portalRef, close, align);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);
  if (!isOpen || !rect || !portalNode) return null;
  return createPortal(
    <IonCard className={className} style={{ ...rect, position: 'absolute' }}>
      {children}
    </IonCard>,
    portalNode,
  );
}

const WIX_WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'] as const;
const getWixWeekday = (isoDate: string): string => {
  const parsed = new Date(`${isoDate}T00:00:00`);
  return WIX_WEEKDAYS[parsed.getDay()] || 'MONDAY';
};

const resolveBlockedEventId = (booking: BookingItem): string | null => {
  const anyBooking = booking as unknown as Record<string, unknown>;
  const agenda = (anyBooking.agendaItem as Record<string, unknown> | undefined) || undefined;

  const candidates = [
    agenda?._id,
    agenda?.eventId,
    anyBooking.eventId,
    anyBooking.calendarEventId,
  ].map((v) => (typeof v === 'string' ? v.trim() : ''));

  const first = candidates.find(Boolean);
  if (first) return first;

  if (typeof booking.id === 'string' && booking.id.startsWith('blocked-')) {
    const stripped = booking.id.replace(/^blocked-/, '').trim();
    if (stripped) return stripped;
  }
  return null;
};

const toDate = (value: string | Date): Date => (value instanceof Date ? value : new Date(value));

const formatTimeLabel = (hhmm: string): string => {
  try {
    return format(new Date(`2000-01-01T${hhmm}:00`), 'h:mm a', { locale: dfLocale() });
  } catch {
    return hhmm;
  }
};

interface BlockedTimeFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialBooking?: BookingItem | null;
  initialDate?: Date;
  onSaved?: () => void;
  onDeleted?: () => void;
}

const BlockedTimeForm: React.FC<BlockedTimeFormProps> = ({
  isOpen,
  onClose,
  initialBooking,
  initialDate,
  onSaved,
  onDeleted,
}) => {
  const { t: tr } = useTranslation();
  const { authState } = useAuth();
  const isEditing = !!initialBooking;

  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [staffOptionsLoading, setStaffOptionsLoading] = useState(true);
  const [optionPicker, setOptionPicker] = useState<OptionPickerKey>(null);

  const [eventId, setEventId] = useState<string | null>(null);
  const [revision, setRevision] = useState<string | undefined>(undefined);
  const [staffId, setStaffId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState<Date>(new Date());
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [availability, setAvailability] = useState<'BUSY' | 'AVAILABLE'>('BUSY');
  const [repeat, setRepeat] = useState(false);
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [repeatUntil, setRepeatUntil] = useState('');
  const [notes, setNotes] = useState('');

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isUntilPickerOpen, setIsUntilPickerOpen] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSaveScope, setShowSaveScope] = useState(false);
  // Part of a repeating series (MASTER/INSTANCE/EXCEPTION) — Delete then asks
  // whether to remove just this occurrence or the whole series.
  const [isRecurring, setIsRecurring] = useState(false);

  const dateFieldRef = useRef<HTMLIonColElement>(null);
  const untilFieldRef = useRef<HTMLIonColElement>(null);
  const startTimeFieldRef = useRef<HTMLIonColElement>(null);
  const endTimeFieldRef = useRef<HTMLIonColElement>(null);
  const staffFieldRef = useRef<HTMLIonColElement>(null);
  const availabilityFieldRef = useRef<HTMLIonColElement>(null);
  const repeatIntervalFieldRef = useRef<HTMLIonColElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);

  const closeDatePicker = useCallback(() => setIsDatePickerOpen(false), []);
  const closeUntilPicker = useCallback(() => setIsUntilPickerOpen(false), []);
  const closeStartTimePicker = useCallback(() => setShowStartTimePicker(false), []);
  const closeEndTimePicker = useCallback(() => setShowEndTimePicker(false), []);
  const closeStaffPicker = useCallback(() => setOptionPicker(null), []);
  const closeAvailabilityPicker = useCallback(() => setOptionPicker(null), []);
  const closeRepeatIntervalPicker = useCallback(() => setOptionPicker(null), []);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load staff options — same resolution path as CalendarDesktop's "Block off
  // time" Staff picker: schedule.externalId (not the nonexistent resourceId)
  // is what maps a schedule back to a staff member's resourceId.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        setStaffOptionsLoading(true);
        const service = DataServiceFactory.getService();
        const [staffList, schedulesResult] = await Promise.all([
          service.staff.getAllStaffMembers(),
          callAdminApi('querySchedules', 'bookings', {}),
        ]);

        const anySchedules = schedulesResult as Record<string, unknown>;
        const scheduleList = (Array.isArray(anySchedules.items) ? anySchedules.items : Array.isArray(anySchedules._items) ? anySchedules._items : []) as Array<Record<string, unknown>>;
        const scheduleIdByResourceId = new Map<string, string>();
        scheduleList.forEach((schedule) => {
          const scheduleId = typeof schedule._id === 'string' ? schedule._id.trim() : (typeof schedule.id === 'string' ? schedule.id.trim() : '');
          const resourceId = typeof schedule.externalId === 'string' ? schedule.externalId.trim() : '';
          if (scheduleId && resourceId) scheduleIdByResourceId.set(resourceId, scheduleId);
        });

        const options: StaffOption[] = staffList
          .map((staff) => {
            const staffRecord = staff as unknown as Record<string, unknown>;
            const id = typeof staff._id === 'string' ? staff._id.trim() : '';
            const name = typeof staff.name === 'string' ? staff.name : '';
            const email = typeof staff.email === 'string' ? staff.email.trim() : '';
            const resourceId = typeof staff.resourceId === 'string' ? staff.resourceId.trim() : '';
            const scheduleIds = Array.isArray(staffRecord.scheduleIds) ? staffRecord.scheduleIds : [];
            const firstStaffSchedule = scheduleIds.find((sid): sid is string => typeof sid === 'string' && sid.trim().length > 0) || '';
            const scheduleId = firstStaffSchedule
              || scheduleIdByResourceId.get(resourceId)
              || scheduleIdByResourceId.get(id)
              || '';
            return { id, name, scheduleId, email };
          })
          .filter((option) => option.id && option.name);

        if (!cancelled) setStaffOptions(options);
      } catch (err) {
        console.error('[BlockedTimeForm] Failed to load staff options:', err);
      } finally {
        if (!cancelled) setStaffOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Populate the form once staff options are ready — either from an existing
  // blocked booking (edit) or fresh defaults (create). Mirrors
  // CalendarDesktop's handleEditBlocked/openCreateBlockedDraft derivation.
  // Computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside an effect, since
  // it's a pure derivation from already-available props/state.
  const [prevFormPopulateInputs, setPrevFormPopulateInputs] = useState({ isOpen, staffOptionsLoading, initialBooking });
  if (
    prevFormPopulateInputs.isOpen !== isOpen ||
    prevFormPopulateInputs.staffOptionsLoading !== staffOptionsLoading ||
    prevFormPopulateInputs.initialBooking !== initialBooking
  ) {
    setPrevFormPopulateInputs({ isOpen, staffOptionsLoading, initialBooking });
    if (isOpen && !staffOptionsLoading) {
      if (initialBooking) {
        const anyBooking = initialBooking as unknown as Record<string, unknown>;
        const agenda = (anyBooking.agendaItem as Record<string, unknown> | undefined) || undefined;

        const id = resolveBlockedEventId(initialBooking);
        const scheduleId = (
          (typeof agenda?.scheduleId === 'string' && agenda.scheduleId)
          || (typeof anyBooking.scheduleId === 'string' && anyBooking.scheduleId)
          || (typeof agenda?.externalScheduleId === 'string' && agenda.externalScheduleId)
          || ''
        );
        const matchedStaff = staffOptions.find((option) => option.scheduleId === scheduleId);
        const transparency = typeof agenda?.transparency === 'string' ? agenda.transparency : undefined;
        const recurrenceType = typeof agenda?.recurrenceType === 'string' ? agenda.recurrenceType : undefined;
        const recurrenceRule = agenda?.recurrenceRule as { interval?: number; until?: string } | undefined;

        const start = toDate(initialBooking.startTime);
        const end = toDate(initialBooking.endTime);

        setEventId(id);
        setRevision(typeof agenda?.revision === 'string' ? agenda.revision : undefined);
        setStaffId(matchedStaff?.id || '');
        setTitle((initialBooking.agendaItem?.title || initialBooking.title || '').trim());
        setDate(start);
        setAllDay(false);
        setStartTime(format(start, 'HH:mm'));
        setEndTime(format(end, 'HH:mm'));
        setAvailability(transparency === 'TRANSPARENT' ? 'AVAILABLE' : 'BUSY');
        setIsRecurring(recurrenceType === 'MASTER' || recurrenceType === 'INSTANCE' || recurrenceType === 'EXCEPTION');
        setRepeat(recurrenceType === 'MASTER' && !!recurrenceRule);
        setRepeatInterval(recurrenceRule?.interval || 1);
        setRepeatUntil(recurrenceRule?.until ? recurrenceRule.until.slice(0, 10) : '');
        setNotes(String(anyBooking.notes || agenda?.notes || '').trim());
      } else {
        const currentUserEmail = authState.user?.email?.toLowerCase();
        const defaultStaff = (currentUserEmail && staffOptions.find((option) => option.email.toLowerCase() === currentUserEmail)) || staffOptions[0];
        const baseDate = initialDate || new Date();
        const roundedHour = new Date(baseDate);
        roundedHour.setMinutes(0, 0, 0);

        setEventId(null);
        setIsRecurring(false);
        setRevision(undefined);
        setStaffId(defaultStaff?.id || '');
        setTitle('');
        setDate(baseDate);
        setAllDay(false);
        setStartTime(format(roundedHour, 'HH:mm'));
        setEndTime(format(new Date(roundedHour.getTime() + 60 * 60 * 1000), 'HH:mm'));
        setAvailability('BUSY');
        setRepeat(false);
        setRepeatInterval(1);
        setRepeatUntil('');
        setNotes('');
      }
    }
  }

  const fetchFallbackScheduleId = useCallback(async (): Promise<string> => {
    try {
      const schedulesResult = await callAdminApi('querySchedules', 'bookings', {});
      const anySchedules = schedulesResult as Record<string, unknown>;
      const scheduleList = (Array.isArray(anySchedules.items) ? anySchedules.items : Array.isArray(anySchedules._items) ? anySchedules._items : []) as Array<Record<string, unknown>>;
      const business = scheduleList.find((s) => typeof s.name === 'string' && /business/i.test(s.name as string));
      const first = scheduleList[0];
      const picked = business || first;
      return typeof picked?._id === 'string' ? picked._id : (typeof picked?.id === 'string' ? picked.id : '');
    } catch {
      return '';
    }
  }, []);

  // scope 'series' (repeating blocks only): the server applies the change to the series' master
  // event, so every occurrence gets it; 'single' changes just the opened occurrence.
  const handleSave = useCallback(async (scope: 'single' | 'series' = 'single') => {
    if (saving) return;
    setSaving(true);
    try {
      const safeTitle = title.trim() || 'Blocked Time';
      const safeDate = format(date, 'yyyy-MM-dd');
      const safeStart = allDay ? '00:00' : startTime;
      const safeEnd = allDay ? '23:59' : endTime;
      const startLocal = `${safeDate}T${safeStart}:00`;
      const endLocal = `${safeDate}T${safeEnd}:00`;

      const eventPayload: Record<string, unknown> = {
        title: safeTitle,
        start: { localDate: startLocal },
        end: { localDate: endLocal },
        transparency: availability === 'AVAILABLE' ? 'TRANSPARENT' : 'OPAQUE',
      };

      if (repeat) {
        eventPayload.recurrenceRule = {
          frequency: 'WEEKLY',
          interval: repeatInterval,
          days: [getWixWeekday(safeDate)],
          ...(repeatUntil ? { until: { localDate: `${repeatUntil}T23:59:00` } } : {}),
        };
      }

      if (notes.trim()) eventPayload.notes = notes.trim();

      const selectedStaffScheduleId = staffOptions.find((option) => option.id === staffId)?.scheduleId;

      if (!eventId) {
        const scheduleId = selectedStaffScheduleId || await fetchFallbackScheduleId();
        if (!scheduleId) {
          window.alert(tr('pages.bookingCalendar.BlockedTimeForm.unableToCreateBlockedTimeMissing'));
          return;
        }
        eventPayload.type = 'DEFAULT';
        eventPayload.scheduleId = scheduleId;
        await callAdminApi('createEvent', 'bookings', eventPayload);
      } else {
        if (scope === 'series') {
          // The opened occurrence's revision and repeat pattern aren't the master's: the server looks those up
          delete eventPayload.recurrenceRule;
        } else if (revision) {
          eventPayload.revision = revision;
        }
        const updateScheduleId = selectedStaffScheduleId || await fetchFallbackScheduleId();
        if (updateScheduleId) eventPayload.scheduleId = updateScheduleId;
        await callAdminApi('updateEvent', 'bookings', { id: eventId, event: eventPayload, scope });
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('[BlockedTimeForm] Failed to save blocked time:', err);
      window.alert(tr('pages.bookingCalendar.BlockedTimeForm.failedToSaveBlockedTime'));
    } finally {
      setSaving(false);
    }
  }, [saving, title, date, allDay, startTime, endTime, availability, repeat, repeatInterval, repeatUntil, notes, staffOptions, staffId, eventId, revision, fetchFallbackScheduleId, onSaved, onClose, tr]);

  const handleDelete = useCallback(async (scope: 'single' | 'series' = 'single') => {
    if (!eventId || deleting) return;
    setDeleting(true);
    try {
      await callAdminApi('deleteEvent', 'bookings', { id: eventId, scope });
      onDeleted?.();
      onClose();
    } catch (err) {
      console.error('[BlockedTimeForm] Failed to delete blocked time:', err);
      window.alert(tr('pages.bookingCalendar.BlockedTimeForm.failedToDeleteBlockedTime'));
    } finally {
      setDeleting(false);
    }
  }, [eventId, deleting, onDeleted, onClose, tr]);

  const selectedStaffName = staffOptions.find((option) => option.id === staffId)?.name
    || (staffOptionsLoading ? tr('pages.bookingCalendar.BlockedTimeForm.loadingStaff') : staffOptions.length === 0 ? tr('pages.bookingCalendar.BlockedTimeForm.noStaffFound') : tr('pages.bookingCalendar.BlockedTimeForm.selectStaff'));

  const optionPickerItems: Array<{ key: string; label: string; active: boolean; onSelect: () => void }> = optionPicker === 'repeatInterval'
    ? [1, 2, 3, 4].map((n) => ({
      key: String(n),
      label: tr('pages.bookingCalendar.BlockedTimeForm.everyWeek', { n, v2: n > 1 ? 's' : '' }),
      active: repeatInterval === n,
      onSelect: () => { setRepeatInterval(n); setOptionPicker(null); },
    }))
    : [];

  return (
    <IonModal className={styles.detailsModal} isOpen={isOpen} onDidDismiss={onClose}>
      {/* Portal target for the Date/Ends/Start/End time dropdowns — see
          usePickerDropdown above for why this needs to exist at all. */}
      <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
      <BasePageWithHeader
        title={isEditing ? tr('pages.bookingCalendar.BlockedTimeForm.editBlockedTime') : tr('pages.bookingCalendar.BlockedTimeForm.newBlockedTime')}
        titleNode={
          <CopyableIdTitle
            title={isEditing ? tr('pages.bookingCalendar.BlockedTimeForm.editBlockedTime') : tr('pages.bookingCalendar.BlockedTimeForm.newBlockedTime')}
            id={eventId}
          />
        }
        toolbarClassName={styles.customToolbar}
        startButtons={
          <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
            <IonIcon slot="icon-only" icon={closeOutline} />
          </IonButton>
        }
        endButtons={isEditing ? (
          <IonButton className={styles.actionButton} onClick={() => setShowDeleteConfirm(true)} disabled={deleting || saving}>
            <IonIcon slot="icon-only" color="danger" icon={trashOutline} />
          </IonButton>
        ) : undefined}
      >
      <IonContent scrollY={false} className={styles.content}>
        <IonGrid className={styles.formContainer}>
        {staffOptionsLoading ? (
          <IonCard className={styles.card}>
            <IonCardContent style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <IonSpinner />
            </IonCardContent>
          </IonCard>
        ) : (
          <IonCard className={styles.card}>
            <IonCardContent className={styles.section}>
                <IonLabel className={styles.sectionTitle}>{tr('pages.bookingCalendar.BlockedTimeForm.schedule')}</IonLabel>

                <IonRow className={styles.fieldRow2}>
                  <IonCol ref={dateFieldRef} className={styles.field}>
                    <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.date')}</IonLabel>
                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.pickerTrigger} onClick={() => setIsDatePickerOpen((o) => !o)}>
                      <IonLabel>{format(date, 'EEE, MMM d', { locale: dfLocale() })}</IonLabel>
                    </IonItem>
                    <PickerDropdown isOpen={isDatePickerOpen} fieldRef={dateFieldRef} portalRef={pickerPortalRef} close={closeDatePicker} align="left" className={styles.calendarDropdown}>
                      <MiniCalendarPicker
                        desktop
                        value={date}
                        onSelect={(d) => { setDate(d); setIsDatePickerOpen(false); }}
                      />
                    </PickerDropdown>
                  </IonCol>
                  <IonCol className={styles.field} style={{ justifyContent: 'flex-end' }}>
                    <IonRow className={styles.checkRow}>
                      <IonCheckbox className={styles.checkbox} checked={repeat} onIonChange={(e) => setRepeat(e.detail.checked)} aria-label={tr('pages.bookingCalendar.BlockedTimeForm.repeat')} />
                      <IonLabel className={styles.checkLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.repeat')}</IonLabel>
                    </IonRow>
                  </IonCol>
                </IonRow>

                {repeat && (
                  <IonRow className={styles.fieldRow2}>
                    <IonCol ref={repeatIntervalFieldRef} className={styles.field}>
                      <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.every')}</IonLabel>
                      <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.pickerTrigger} onClick={() => setOptionPicker((p) => (p === 'repeatInterval' ? null : 'repeatInterval'))}>
                        <IonLabel>{tr('pages.bookingCalendar.BlockedTimeForm.week', { repeatInterval })}{repeatInterval > 1 ? 's' : ''}</IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={optionPicker === 'repeatInterval'} fieldRef={repeatIntervalFieldRef} portalRef={pickerPortalRef} close={closeRepeatIntervalPicker} align="left" className={styles.calendarDropdown}>
                        <IonList lines="none" className={styles.dropdownList}>
                          {optionPickerItems.map((item) => (
                            <IonItem
                              key={item.key}
                              button
                              detail={false}
                              lines="none"
                              className={`${styles.dropdownItem}${item.active ? ` ${styles.dropdownItemActive}` : ''}`}
                              onClick={item.onSelect}
                            >
                              <IonLabel>{item.label}</IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      </PickerDropdown>
                    </IonCol>
                    <IonCol ref={untilFieldRef} className={styles.field}>
                      <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.ends')}</IonLabel>
                      <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.pickerTrigger} onClick={() => setIsUntilPickerOpen((o) => !o)}>
                        <IonLabel>{repeatUntil ? format(new Date(`${repeatUntil}T00:00:00`), 'MMM d, yyyy', { locale: dfLocale() }) : tr('pages.bookingCalendar.BlockedTimeForm.never')}</IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={isUntilPickerOpen} fieldRef={untilFieldRef} portalRef={pickerPortalRef} close={closeUntilPicker} align="right" className={styles.calendarDropdown}>
                        <MiniCalendarPicker
                          desktop
                          value={repeatUntil ? new Date(`${repeatUntil}T00:00:00`) : date}
                          onSelect={(d) => { setRepeatUntil(format(d, 'yyyy-MM-dd')); setIsUntilPickerOpen(false); }}
                        />
                      </PickerDropdown>
                    </IonCol>
                  </IonRow>
                )}

                <IonRow className={styles.checkRow}>
                  <IonCheckbox className={styles.checkbox} checked={allDay} onIonChange={(e) => setAllDay(e.detail.checked)} aria-label={tr('pages.bookingCalendar.BlockedTimeForm.allDay')} />
                  <IonLabel className={styles.checkLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.allDay')}</IonLabel>
                </IonRow>

                {!allDay && (
                  <IonRow className={styles.fieldRow2}>
                    <IonCol ref={startTimeFieldRef} className={styles.field}>
                      <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.startTime')}</IonLabel>
                      <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.pickerTrigger} onClick={() => setShowStartTimePicker((o) => !o)}>
                        <IonLabel>{formatTimeLabel(startTime)}</IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={showStartTimePicker} fieldRef={startTimeFieldRef} portalRef={pickerPortalRef} close={closeStartTimePicker} align="left" className={styles.timeDropdown}>
                        <GcalTimePicker
                          desktop
                          value={startTime}
                          onSelect={(t) => { setStartTime(t); setShowStartTimePicker(false); }}
                          onClose={() => setShowStartTimePicker(false)}
                        />
                      </PickerDropdown>
                    </IonCol>
                    <IonCol ref={endTimeFieldRef} className={styles.field}>
                      <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.endTime')}</IonLabel>
                      <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.pickerTrigger} onClick={() => setShowEndTimePicker((o) => !o)}>
                        <IonLabel>{formatTimeLabel(endTime)}</IonLabel>
                      </IonItem>
                      <PickerDropdown isOpen={showEndTimePicker} fieldRef={endTimeFieldRef} portalRef={pickerPortalRef} close={closeEndTimePicker} align="right" className={styles.timeDropdown}>
                        <GcalTimePicker
                          desktop
                          value={endTime}
                          onSelect={(t) => { setEndTime(t); setShowEndTimePicker(false); }}
                          onClose={() => setShowEndTimePicker(false)}
                        />
                      </PickerDropdown>
                    </IonCol>
                  </IonRow>
                )}

                <IonRow className={styles.fieldRow2}>
                  <IonCol ref={staffFieldRef} className={styles.field}>
                    <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.staff')}</IonLabel>
                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.pickerTrigger} onClick={() => setOptionPicker((p) => (p === 'staff' ? null : 'staff'))}>
                      <IonLabel>{selectedStaffName}</IonLabel>
                    </IonItem>
                    <PickerDropdown isOpen={optionPicker === 'staff'} fieldRef={staffFieldRef} portalRef={pickerPortalRef} close={closeStaffPicker} align="left" className={styles.calendarDropdown}>
                      <IonList lines="none" className={styles.dropdownList}>
                        {staffOptions.map((option) => (
                          <IonItem
                            key={option.id}
                            button
                            detail={false}
                            lines="none"
                            className={`${styles.dropdownItem}${staffId === option.id ? ` ${styles.dropdownItemActive}` : ''}`}
                            onClick={() => { setStaffId(option.id); setOptionPicker(null); }}
                          >
                            <IonLabel>{option.name}</IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    </PickerDropdown>
                  </IonCol>
                  <IonCol ref={availabilityFieldRef} className={styles.field}>
                    <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.availability')}</IonLabel>
                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" className={styles.pickerTrigger} onClick={() => setOptionPicker((p) => (p === 'availability' ? null : 'availability'))}>
                      <IonLabel>{availability === 'AVAILABLE' ? tr('pages.bookingCalendar.BlockedTimeForm.available') : tr('pages.bookingCalendar.BlockedTimeForm.busy')}</IonLabel>
                    </IonItem>
                    <PickerDropdown isOpen={optionPicker === 'availability'} fieldRef={availabilityFieldRef} portalRef={pickerPortalRef} close={closeAvailabilityPicker} align="right" className={styles.calendarDropdown}>
                      <IonList lines="none" className={styles.dropdownList}>
                        {(['BUSY', 'AVAILABLE'] as const).map((value) => (
                          <IonItem
                            key={value}
                            button
                            detail={false}
                            lines="none"
                            className={`${styles.dropdownItem}${availability === value ? ` ${styles.dropdownItemActive}` : ''}`}
                            onClick={() => { setAvailability(value); setOptionPicker(null); }}
                          >
                            <IonLabel>{value === 'BUSY' ? tr('pages.bookingCalendar.BlockedTimeForm.busy') : tr('pages.bookingCalendar.BlockedTimeForm.available')}</IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    </PickerDropdown>
                  </IonCol>
                </IonRow>
              </IonCardContent>

              <IonCardContent className={styles.section}>
                <IonLabel className={`${styles.sectionTitle} ${styles.sectionTitleDetails}`}>{tr('pages.bookingCalendar.BlockedTimeForm.details')}</IonLabel>
                <IonCol className={styles.stackedField}>
                  <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.title')}</IonLabel>
                  <IonInput
                    className={styles.titleInput}
                    value={title}
                    placeholder={tr('pages.bookingCalendar.BlockedTimeForm.eGPrivateEvent')}
                    onIonInput={(e) => setTitle(e.detail.value || '')}
                  />
                </IonCol>
                <IonCol className={styles.stackedField}>
                  <IonLabel className={styles.fieldLabel}>{tr('pages.bookingCalendar.BlockedTimeForm.note')}</IonLabel>
                  <IonTextarea
                    className={styles.notesInput}
                    value={notes}
                    rows={3}
                    autoGrow
                    onIonInput={(e) => setNotes(e.detail.value || '')}
                  />
                </IonCol>
                <IonButtons className={styles.editActions}>
                  <IonButton fill="solid" className={styles.cancelButton} onClick={onClose} disabled={saving || deleting}>{tr('pages.bookingCalendar.BlockedTimeForm.cancel')}
                  </IonButton>
                  <IonButton fill="solid" className={styles.saveButton} onClick={() => { if (eventId && isRecurring) setShowSaveScope(true); else void handleSave(); }} disabled={saving || deleting || staffOptionsLoading}>
                    {saving ? <IonSpinner name="dots" /> : tr('pages.bookingCalendar.BlockedTimeForm.save')}
                  </IonButton>
                </IonButtons>
              </IonCardContent>
            </IonCard>
        )}
        </IonGrid>
      </IonContent>
      </BasePageWithHeader>

      <IonAlert
        isOpen={showSaveScope}
        onDidDismiss={() => setShowSaveScope(false)}
        header={tr('pages.bookingCalendar.BlockedTimeForm.saveRepeatingBlockedTime')}
        message={tr('pages.bookingCalendar.BlockedTimeForm.thisBlockedTimeRepeatsSaveOnly')}
        cssClass="logout-alert"
        buttons={[
          { text: tr('pages.bookingCalendar.BlockedTimeForm.cancel'), role: 'cancel', handler: () => setShowSaveScope(false) },
          { text: tr('pages.bookingCalendar.BlockedTimeForm.thisEventOnly'), handler: () => { setShowSaveScope(false); void handleSave('single'); } },
          { text: tr('pages.bookingCalendar.BlockedTimeForm.allEventsInSeries'), handler: () => { setShowSaveScope(false); void handleSave('series'); } },
        ]}
      />

      <IonAlert
        isOpen={showDeleteConfirm}
        onDidDismiss={() => setShowDeleteConfirm(false)}
        header={tr('pages.bookingCalendar.BlockedTimeForm.deleteBlockedTime')}
        message={isRecurring
          ? tr('pages.bookingCalendar.BlockedTimeForm.thisBlockedTimeRepeatsDeleteOnly')
          : tr('pages.bookingCalendar.BlockedTimeForm.areYouSureYouWantTo')}
        cssClass="logout-alert"
        buttons={isRecurring ? [
          { text: tr('pages.bookingCalendar.BlockedTimeForm.cancel'), role: 'cancel', handler: () => setShowDeleteConfirm(false) },
          { text: tr('pages.bookingCalendar.BlockedTimeForm.thisEventOnly'), handler: () => { setShowDeleteConfirm(false); void handleDelete('single'); } },
          { text: tr('pages.bookingCalendar.BlockedTimeForm.allEventsInSeries'), handler: () => { setShowDeleteConfirm(false); void handleDelete('series'); } },
        ] : [
          { text: tr('pages.bookingCalendar.BlockedTimeForm.cancel'), role: 'cancel', handler: () => setShowDeleteConfirm(false) },
          { text: tr('pages.bookingCalendar.BlockedTimeForm.delete'), handler: () => { setShowDeleteConfirm(false); void handleDelete('single'); } },
        ]}
      />
    </IonModal>
  );
};

export default BlockedTimeForm;
